import { getSkillFrontmatterErrors } from '@aline/common';
import type { parseSkillMarkdown, SkillFrontmatterError } from '@aline/common';
import type { Apollo } from 'src/gql/generated';

import type { SkillAttachmentChanges } from './skill-attachment-editor';
import { getFinalSkillName } from './skill-editor-content';
import { getDraftFilesHint, getPublishBlocker } from './skill-editor-draft';
import type { SkillSaveError } from './skill-save-error';
import { getSkillAncestorFolderTreeIds } from './skill-tree-ids';

type ParsedSkillMarkdown = ReturnType<typeof parseSkillMarkdown>;

/** Every folder an attachment path sits in, parents included, as tree item ids. */
export function getAttachmentFolderIds(paths: string[]): string[] {
  return [...new Set(paths.flatMap(getSkillAncestorFolderTreeIds))];
}

/** The attachment changes as the save mutation takes them. */
export function getAttachmentInput(changes: SkillAttachmentChanges) {
  return {
    ...changes,
    documentSnapshots: changes.documentSnapshots?.map(({ documentId, folderPath, name }) => ({
      documentId,
      folderPath,
      name,
    })),
  };
}

/** Frontmatter errors to show: the saving rules, plus a missing name or description after Run. */
export function getVisibleFrontmatterErrors({
  parsedContent,
  runAttempted,
}: {
  parsedContent: ParsedSkillMarkdown;
  runAttempted: boolean;
}): SkillFrontmatterError[] {
  if (!parsedContent.ok) return [];
  const { frontmatter } = parsedContent;
  const errors = getSkillFrontmatterErrors({
    ...frontmatter,
    name: getFinalSkillName(frontmatter.name),
  });
  if (!runAttempted) return errors;
  if (!frontmatter.name.trim())
    errors.push({ field: 'name', message: 'Add a name to run this skill.' });
  if (!frontmatter.description.trim())
    errors.push({ field: 'description', message: 'Add a description to run this skill.' });
  return errors;
}

/** Why the skill cannot run yet, or '' when it can. */
export function getRunHint({
  contentError,
  parsedContent,
}: {
  contentError: string | null;
  parsedContent: ParsedSkillMarkdown;
}): string {
  if (!parsedContent.ok || contentError) return 'Fix the skill errors to run.';
  const hasName = Boolean(parsedContent.frontmatter.name.trim());
  const hasDescription = Boolean(parsedContent.frontmatter.description.trim());
  if (!hasName && !hasDescription) return 'Add a name and description to run.';
  if (!hasName) return 'Add a name to run.';
  if (!hasDescription) return 'Add a description to run.';
  return '';
}

/** The field Run sends the author to, or null when the skill can run. */
export function getRunFocusTarget({
  contentError,
  parsed,
}: {
  contentError: string | null;
  parsed: ParsedSkillMarkdown;
}): string | null {
  if (!parsed.ok) return 'SKILL.md source';
  const { description, name } = parsed.frontmatter;
  if (!contentError && name.trim() && description.trim()) return null;
  const firstError = getSkillFrontmatterErrors(parsed.frontmatter).at(0)?.field;
  if (!name.trim() || firstError === 'name') return 'Skill name';
  if (!description.trim() || firstError === 'description') return 'Skill description';
  return firstError ?? 'Skill name';
}

/** Why Test or Run is unavailable, or empty when it can be pressed. */
export function getRunBlocker({
  outdated,
  runHint,
  runsDraft,
  testFilesHint,
}: {
  outdated: boolean;
  runHint: string;
  runsDraft: boolean;
  testFilesHint: string;
}): string {
  if (outdated) return runsDraft ? 'Reload the skill to test it.' : 'Reload the skill to run it.';
  if (runHint) return runsDraft ? runHint.replace(/ to run\.$/, ' to test.') : runHint;
  return testFilesHint;
}

/** What Test, Run and Publish can do with the skill, and why not when they can't. */
export function getSkillRunState({
  canEdit,
  dirty,
  outdated,
  runHint,
  skill,
}: {
  canEdit: boolean;
  dirty: boolean;
  outdated: boolean;
  runHint: string;
  skill: Apollo.AgentSkillSimpleFragment;
}) {
  const filesHint = canEdit ? getDraftFilesHint(skill.attachments) : '';
  // Only the draft's files are checked: an unchanged published skill is already processed.
  const testFilesHint = skill.hasUnpublishedChanges ? filesHint : '';
  /**
   * The author tests a draft; with nothing unpublished, the button runs the published skill.
   * An edit not saved yet counts as unpublished, so the first keystroke already reads Test
   * rather than offering to run the version the author is changing.
   */
  const runsDraft =
    canEdit && (!skill.isPublished || Boolean(skill.hasUnpublishedChanges) || dirty);

  return {
    publishBlocker: getPublishBlocker({ filesHint, runHint, skill }),
    runBlocker: getRunBlocker({ outdated, runHint, runsDraft, testFilesHint }),
    runsDraft,
    testFilesHint,
  };
}

export function getStatusLabel({
  contentError,
  dirty,
  saveError,
  saving,
}: {
  contentError: string | null;
  dirty: boolean;
  saveError: SkillSaveError | null;
  saving: boolean;
}): string {
  if (saveError) return 'Could not save';
  if (dirty && contentError) return 'Check the highlighted errors';
  if (saving || dirty) return 'Saving...';
  return 'Saved';
}
